'use client';

import { forwardRef, useContext, useMemo } from 'react';

import { css, cx, splitResponsiveVariant } from '@/styled-system/css';

import { useDisabledInteractions } from '../../hooks/use-disabled-interactions';
import { ButtonGroupContext } from '../button-group/context/button-group.context';
import { Slot } from '../slot';
import { ButtonRootContext } from './context/button-root.context';
import { buttonRecipe } from './styles/button-root.styles';
import type { ButtonRootPropsInterface } from './types/button-root.types';

/**
 * @since 10.0.0
 */
export const ButtonRoot = forwardRef<HTMLButtonElement, ButtonRootPropsInterface>(
  (props: ButtonRootPropsInterface, forwardedRef) => {
    const buttonGroupContext = useContext(ButtonGroupContext);
    const {
      asChild = false,
      tone = 'brand',
      emphasis = 'outlined',
      children,
      className,
      css: cssProp,
      disabled = false,
      fluid = buttonGroupContext.fluid || false,
      onImpact = false,
      size = buttonGroupContext.size || 'md',
      loading = false,
      type = 'button',
      ...rest
    } = props;

    const responsiveFluid = splitResponsiveVariant('fluid', fluid);

    const styles = css(
      buttonRecipe.raw({
        tone: buttonGroupContext.tone || tone,
        emphasis: buttonGroupContext.emphasis || emphasis,
        disabled,
        onImpact,
        loading,
        size: buttonGroupContext.size || size,
        ...responsiveFluid,
      }),
      cssProp,
    );
    const Component = asChild ? Slot : 'button';

    // Loading buttons stay focusable so users don't lose their place.
    const loadingDisabledInteractionsProps = useDisabledInteractions({ focusable: true });

    // Real `<button>` uses the native `disabled` attribute; when rendered as an
    // anchor via `asChild`, fall back to aria-disabled + tabIndex + event blocking.
    const disabledInteractionsProps = useDisabledInteractions({ focusable: false });

    const contextValue = useMemo(
      () => ({
        tone: buttonGroupContext.tone || tone,
        emphasis: buttonGroupContext.emphasis || emphasis,
        disabled,
        onImpact,
        size: buttonGroupContext.size || size,
        loading,
      }),
      [
        buttonGroupContext.tone,
        tone,
        buttonGroupContext.emphasis,
        emphasis,
        disabled,
        onImpact,
        buttonGroupContext.size,
        size,
        loading,
      ],
    );

    return (
      <ButtonRootContext.Provider value={contextValue}>
        <Component
          ref={forwardedRef}
          data-c11n-component="Button.Root"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', 'group', styles, className)}
          disabled={!asChild && disabled ? true : undefined}
          type={asChild ? undefined : loading && type === 'submit' ? 'button' : type}
          {...rest}
          {...(asChild && disabled
            ? disabledInteractionsProps
            : loading
              ? loadingDisabledInteractionsProps
              : {})}
        >
          {children}
        </Component>
      </ButtonRootContext.Provider>
    );
  },
);

ButtonRoot.displayName = 'Button.Root';
